// Client-side encryption utility for protecting sensitive documents
// Uses Web Crypto API (AES-GCM) for secure encryption

/**
 * Derives an encryption key from a password using PBKDF2
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);
  
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveKey']
  );
  
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypts data with a password
 * @returns Base64-encoded encrypted data with salt and IV prepended
 */
export async function encryptData(data: string, password: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  
  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  // Derive key from password
  const key = await deriveKey(password, salt);
  
  // Encrypt
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    dataBuffer
  );
  
  // Combine salt + IV + encrypted data
  const combined = new Uint8Array(salt.length + iv.length + encryptedBuffer.byteLength);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encryptedBuffer), salt.length + iv.length);
  
  // Convert to base64 (process in chunks to avoid "too many arguments" error)
  let binaryString = '';
  const chunkSize = 8192;
  for (let i = 0; i < combined.length; i += chunkSize) {
    const chunk = combined.slice(i, i + chunkSize);
    binaryString += String.fromCharCode(...chunk);
  }
  return btoa(binaryString);
}

/**
 * Decrypts data with a password
 * @throws Error if password is incorrect
 */
export async function decryptData(encryptedBase64: string, password: string): Promise<string> {
  // Decode from base64
  const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
  
  // Extract salt, IV, and encrypted data
  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 28);
  const encryptedData = combined.slice(28);
  
  // Derive key from password
  const key = await deriveKey(password, salt);
  
  try {
    // Decrypt
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedData
    );
    
    const decoder = new TextDecoder();
    return decoder.decode(decryptedBuffer);
  } catch (err) {
    throw new Error('Incorrect password or corrupted data');
  }
}

/**
 * Encrypts a file (converts to base64 first)
 */
export async function encryptFile(file: File, password: string): Promise<string> {
  console.log('[encryptFile] Starting encryption for:', file.name, 'Password length:', password.length);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target?.result as string;
        console.log('[encryptFile] File read complete, base64 length:', base64.length);
        console.log('[encryptFile] Calling encryptData...');
        const encrypted = await encryptData(base64, password);
        console.log('[encryptFile] Encryption complete, encrypted length:', encrypted.length);
        resolve(encrypted);
      } catch (err) {
        console.error('[encryptFile] Error during encryption:', err);
        reject(err);
      }
    };
    reader.onerror = (err) => {
      console.error('[encryptFile] FileReader error:', err);
      reject(err);
    };
    console.log('[encryptFile] Starting file read...');
    reader.readAsDataURL(file);
  });
}

/**
 * Decrypts a file back to a data URL
 */
export async function decryptFile(encryptedData: string, password: string): Promise<string> {
  return await decryptData(encryptedData, password);
}
